import { normalisePhone } from "@hakili/domain-types";
import { t } from "@hakili/i18n";
import { Button, Card } from "@hakili/ui";
import { useState } from "react";

/**
 * `P0-IDN-044`'s staff-visible half: **where the chargé reads the code during a
 * demonstration, and nowhere else.**
 *
 * **The plan is silent and this is the smallest honest thing.** Round 178's
 * finding recorded that no clause anywhere says how a demonstration client
 * receives their code while `UnwiredOtpSender` sends nothing. The architect
 * granted the route; this is the four fields a person needs to use it, on the
 * one console screen that exists, and it is deliberately not a screen of its
 * own: §2 of round 179's order says *"no new screen estate beyond what the
 * sitting requires"*, and a sitting requires a number, a control, and an answer.
 *
 * **Gated identically to the route, and the gate is the SERVER's.**
 * `demo_devices_enabled` is evaluated server side and arrives on `/me` , 4.7.3's
 * own design, *"exposed to the frontend through `/api/v1/me`"*. So this block
 * renders on the flag the API reports and never on a build-time constant: a
 * founder who turns the flag off sees it disappear on the next reload, and a
 * production console never receives a true to render on.
 *
 * **It is a courtesy and never the enforcement.** Hiding a control is not
 * security , the route's own four conditions are, and they run whatever this
 * component does. If somebody reached the endpoint with this block never
 * rendered, the boot gates, the principal check and the right check would all
 * still be in front of them.
 *
 * **Danger sentence.** This block displays a credential on a screen in a room.
 * That is its whole purpose and also its whole risk, so it shows ONE number's
 * code at a time, on an explicit action, and keeps nothing: no list, no history,
 * no storage. A panel that accumulated the codes it had read would be a
 * credential store built by accident, on the machine most likely to be
 * projected onto a wall.
 */

export function Choreographie({
  lireLeCode,
}: {
  /** Reads the last code the API minted for a number. `null` when it answers 404. */
  readonly lireLeCode: (phone: string) => Promise<{ channel: string; code: string } | null>;
}): React.JSX.Element {
  const [numero, setNumero] = useState("");
  const [reponse, setReponse] = useState<{ channel: string; code: string } | null>(null);
  const [absent, setAbsent] = useState(false);
  const [enCours, setEnCours] = useState(false);

  return (
    <Card shape="console" className="flex flex-col gap-3">
      <h2 className="text-base font-semibold text-gray-900">{t("console.choreographie.titre")}</h2>
      <p className="text-sm text-gray-500">{t("console.choreographie.explication")}</p>
      <label className="flex flex-col gap-1 text-sm text-gray-500" htmlFor="choreographie-numero">
        {t("console.choreographie.numero")}
        <input
          id="choreographie-numero"
          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
          inputMode="tel"
          value={numero}
          onChange={(event) => {
            setNumero(event.target.value);
            // A new number clears the old answer rather than leaving it under a
            // field it no longer belongs to.
            setReponse(null);
            setAbsent(false);
          }}
        />
      </label>
      <Button
        variant="secondary"
        onClick={() => {
          setEnCours(true);
          // **One map, one spelling.** The API stores the last code under
          // `normalisePhone(phone)` and this route looks it up with no
          // normalisation of its own, so a number typed here with the spaces a
          // person naturally uses , `+223 79 79 79 79` , would ask for a key
          // the client never wrote. The card therefore spells the number the
          // same way the client does, using the SAME exported function rather
          // than a second regex that can drift from it. `trim()` stays: it
          // removes what a paste leaves at the ends, which is a different job.
          void lireLeCode(normalisePhone(numero.trim()))
            .then((lu) => {
              setReponse(lu);
              setAbsent(lu === null);
            })
            .finally(() => {
              setEnCours(false);
            });
        }}
      >
        {enCours ? t("console.choreographie.lecture") : t("console.choreographie.lire")}
      </Button>
      {reponse === null ? null : (
        <p role="status" className="text-lg font-semibold tracking-widest text-gray-900">
          {reponse.code}
        </p>
      )}
      {absent ? (
        <p role="alert" className="text-sm text-gray-500">
          {t("console.choreographie.aucun")}
        </p>
      ) : null}
    </Card>
  );
}
